import { useEffect, useState } from 'react';
import type {
  AnyClaimPaymentOut,
  SyncInfo,
} from '../../client/portal/types.gen';
import {
  getPaymentsFromOpenClaimCasesService,
  syncPaymentsWithSebService,
} from '../../services/portalServices';
import styled, { css, keyframes } from 'styled-components';
import PaymentStatusInformation from './components/PaymentStatusInformation';
import SebHealthBanner from './components/SebHealthBanner';
import TabContent from '../../components/TabContent';
import Tag from '../../components/Tag';
import PaymentsTable from './components/PaymentsTable';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import { useNotification } from '../../context/notificationContext';
import ContentWrapper from '../../components/ContentWrapper';

type PaymentCategoriesTag =
  | 'PaymentSebAcknowledged'
  | 'PaymentSebBooked'
  | 'PaymentSebPending'
  | 'PaymentSebRejected'
  | 'PaymentScheduled'
  | 'PaymentUnsigned'
  | 'SanctionsHeld'
  | 'PaymentOther';

type PaymentCategories = {
  [tag in PaymentCategoriesTag]: {
    name: string;
    payments: AnyClaimPaymentOut[];
  };
};

const initialPaymentsCategories: PaymentCategories = {
  PaymentUnsigned: { name: 'Unsigned', payments: [] },
  PaymentScheduled: { name: 'Scheduled', payments: [] },
  SanctionsHeld: { name: 'Sanctions held', payments: [] },
  PaymentSebPending: { name: 'Pending', payments: [] },
  PaymentSebAcknowledged: { name: 'Acknowledged', payments: [] },
  PaymentSebBooked: { name: 'Booked', payments: [] },
  PaymentOther: { name: 'Other', payments: [] },
  PaymentSebRejected: { name: 'Rejected', payments: [] },
};

const PaymentsView = () => {
  const { addNotification, removeNotificationWithDelay } = useNotification();
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [healthRefreshToken, setHealthRefreshToken] = useState<number>(0);
  const [payments, setPayments] = useState<AnyClaimPaymentOut[]>([]);
  const [paymentCategories, setPaymentCategories] = useState<PaymentCategories>(
    initialPaymentsCategories,
  );

  const getPayments = async () => {
    setPayments(await getPaymentsFromOpenClaimCasesService());
  };

  useEffect(() => {
    getPayments();
  }, []);

  useEffect(() => {
    setPaymentCategories(
      payments.reduce((a: PaymentCategories, b: AnyClaimPaymentOut) => {
        if (b.payment.sanctionsStatus.tag === 'SanctionsHeld') {
          return {
            ...a,
            SanctionsHeld: {
              ...a['SanctionsHeld'],
              payments: [...a['SanctionsHeld'].payments, b],
            },
          };
        }

        if (b.payment.status.tag === 'PaymentSeb') {
          if (b.payment.status.paymentSebStatus.tag === 'PaymentSebFulfilled') {
            return a;
          }

          // PaymentSebRemoved falls through to "Other" below.
          if (
            b.payment.status.paymentSebStatus.tag === 'PaymentSebPending' ||
            b.payment.status.paymentSebStatus.tag === 'PaymentSebBooked' ||
            b.payment.status.paymentSebStatus.tag ===
              'PaymentSebAcknowledged' ||
            b.payment.status.paymentSebStatus.tag === 'PaymentSebRejected'
          ) {
            return {
              ...a,
              [b.payment.status.paymentSebStatus.tag]: {
                ...a[b.payment.status.paymentSebStatus.tag],
                payments: [
                  ...a[b.payment.status.paymentSebStatus.tag].payments,
                  b,
                ],
              },
            };
          }
        }

        if (
          b.payment.status.tag === 'PaymentUnsigned' ||
          b.payment.status.tag === 'PaymentScheduled'
        ) {
          return {
            ...a,
            [b.payment.status.tag]: {
              ...a[b.payment.status.tag],
              payments: [...a[b.payment.status.tag].payments, b],
            },
          };
        }

        return {
          ...a,
          PaymentOther: {
            ...a['PaymentOther'],
            payments: [...a['PaymentOther'].payments, b],
          },
        };
      }, initialPaymentsCategories),
    );
  }, [payments]);

  const syncPaymentsWithSeb = async () => {
    setIsSyncing(true);

    try {
      const result = await syncPaymentsWithSebService();
      const failed = result.filter(
        (info): info is Extract<SyncInfo, { tag: 'SyncFailure' }> =>
          info.tag === 'SyncFailure',
      );
      const succeeded = result.filter(
        (info): info is Extract<SyncInfo, { tag: 'SyncSuccess' }> =>
          info.tag === 'SyncSuccess',
      );

      if (failed.length > 0) {
        removeNotificationWithDelay(
          addNotification(
            `Sync with SEB failed for: ${failed
              .map(info => info.partnerName)
              .join(', ')}`,
            'alert',
          ),
        );
      } else {
        const pain002Count = succeeded.reduce(
          (n, info) => n + info.pain002s.length,
          0,
        );
        const transactionCount = succeeded.reduce(
          (n, info) => n + info.transactions.length,
          0,
        );

        removeNotificationWithDelay(
          addNotification(
            `Sync with SEB completed: ${succeeded.length} partners, ${pain002Count} status reports and ${transactionCount} transactions sent.`,
            'success',
          ),
        );
      }
    } catch (error) {
      removeNotificationWithDelay(
        addNotification(
          `Something went wrong when trying to sync with SEB: ${
            error instanceof Error ? error.message : String(error)
          }`,
          'alert',
        ),
      );
    } finally {
      setIsSyncing(false);
      getPayments();
      setHealthRefreshToken(token => token + 1);
    }
  };

  return (
    <ContentWrapper>
      <header>
        <h1>Payments</h1>
      </header>
      <SebHealthBanner refreshToken={healthRefreshToken} />
      <PaymentStatusInformation />
      <ReportButtonsWrapper>
        <Button onClick={syncPaymentsWithSeb} disabled={isSyncing}>
          <SyncIconWrapper isSyncing={isSyncing}>
            <Icon iconName='faArrowsRotate' />
          </SyncIconWrapper>{' '}
          Sync payments
        </Button>
      </ReportButtonsWrapper>
      <TabContent
        contents={Object.entries(paymentCategories)
          .filter(
            ([, category]) =>
              (category.name !== 'Other' &&
                category.name !== 'Sanctions held') ||
              category.payments.length > 0,
          )
          .map(([key, category]) => ({
            item: {
              content: (
                <TabContentWrapper>
                  {category.name}{' '}
                  {category.payments.length > 0 && (
                    <div>
                      <Tag
                        color={
                          category.name === 'Rejected' ||
                          category.name === 'Sanctions held'
                            ? 'red'
                            : 'blue'
                        }
                        size='small'
                      >
                        {category.payments.length}
                      </Tag>
                    </div>
                  )}
                </TabContentWrapper>
              ),
            },
            component:
              category.payments.length > 0 ? (
                <PaymentsTable
                  tag={key}
                  category={key}
                  payments={category.payments}
                  getPayments={getPayments}
                />
              ) : (
                <span>No {category.name.toLowerCase()} payments</span>
              ),
          }))}
        size='medium'
      />
    </ContentWrapper>
  );
};

export default PaymentsView;

const ReportButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const TabContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const SyncIconWrapper = styled.div<{ isSyncing: boolean }>`
  display: inline-block;
  animation: ${({ isSyncing }) =>
    isSyncing
      ? css`
          ${rotate} 2s linear infinite
        `
      : ''};
`;
